import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Sparkles, Star, Search as SearchIcon, Calendar, Heart, Gift } from "lucide-react";
import api, { waLink } from "../lib/api";
import { Button } from "../components/ui/button";
import { PackageCard } from "../components/PackageCard";

import { QuickLinks } from "../components/QuickLinks";
import Testimonials from "../components/Testimonials";

import { motion, useScroll, useTransform } from "framer-motion";

const Hero = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const y2 = useTransform(scrollY, [0, 500], [0, -100]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative overflow-hidden min-h-[70vh] flex items-start pt-28 sm:pt-40" data-testid="hero-section">
      <motion.div style={{ y: y1 }} className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1777119914534-81d2ef0e93e9?auto=format&fit=crop&q=60&w=1200" alt="YoursDreamDecoration Hero" className="w-full h-full object-cover scale-110" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </motion.div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          style={{ y: y2, opacity }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/20 backdrop-blur-md border border-primary/30 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground mb-8">
            <Sparkles className="w-3 h-3 text-primary" /> Premium Celebration Experts
          </span>
          <h1 className="font-serif-display text-6xl sm:text-7xl lg:text-8xl text-white leading-[0.9] font-medium tracking-tighter">
            Crafting <motion.span 
              initial={{ opacity: 0, rotate: -5 }}
              animate={{ opacity: 1, rotate: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="italic text-primary drop-shadow-2xl inline-block"
            >Magic</motion.span> <br />
            For Your Dreams.
          </h1>
          <p className="mt-8 max-w-xl text-lg sm:text-xl text-white/80 leading-relaxed font-medium">
            India's most loved decoration service for birthdays, anniversaries, and romantic surprises.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <a href={waLink("Hi, I want to book a decoration setup")} target="_blank" rel="noreferrer">
              <Button size="lg" className="h-16 rounded-2xl bg-primary hover:bg-primary/90 gap-3 px-8 text-lg font-bold shadow-2xl shadow-primary/40 transition-all hover:scale-105 active:scale-95" data-testid="hero-whatsapp-btn">
                <MessageCircle className="w-6 h-6" /> Chat on WhatsApp
              </Button>
            </a>
            <Link to="/category/couple-s-special">
              <Button size="lg" variant="outline" className="h-16 rounded-2xl border-2 border-white/30 text-white bg-white/10 hover:bg-white hover:text-slate-900 px-8 text-lg font-bold backdrop-blur-sm transition-all" data-testid="hero-view-packages-btn">
                View Packages <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Wavy Divider */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] transform rotate-180">
        <svg className="relative block w-[calc(100%+1.3px)] h-[80px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-background"></path>
        </svg>
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section className="py-20 bg-card/40" data-testid="how-it-works">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mb-12">
        <span className="text-sm uppercase tracking-[0.2em] text-primary">How it works</span>
        <h2 className="mt-2 font-serif-display text-4xl sm:text-5xl">Three steps to your perfect setup</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { icon: SearchIcon, title: "Browse or Chat", text: "Explore packages or just tell our assistant what you want." },
          { icon: Calendar, title: "Pick & Book", text: "Choose date, location & add-ons. Confirm in minutes." },
          { icon: Gift, title: "Relax & Celebrate", text: "Our team arrives, sets up & creates magic for you." },
        ].map((s, i) => (
          <div key={i} className="bg-background rounded-3xl p-8 border border-border" data-testid={`step-${i}`}>
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
              <s.icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Step {i + 1}</div>
            <h3 className="font-serif-display text-2xl mb-2">{s.title}</h3>
            <p className="text-sm text-muted-foreground">{s.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Marquee = ({ packages }) => {
  const items = [...packages, ...packages];
  return (
    <section className="py-16 overflow-hidden" data-testid="under-3500-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end justify-between mb-8">
        <div>
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-secondary font-medium">Under Budget</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-1">Under ₹3500 magic</h2>
        </div>
      </div>
      <div className="relative">
        <div className="flex gap-5 animate-marquee w-max">
          {items.map((p, i) => (
            <div key={`${p.slug}-${i}`} className="w-72 shrink-0">
              <PackageCard pkg={p} badgeLabel="Under Budget" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HotDeals = ({ packages }) => (
  <section className="py-16" data-testid="hot-deals-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-primary font-medium">🔥 Hot Deals</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-1">Trending this week</h2>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {packages.slice(0, 4).map((p) => <PackageCard key={p.slug} pkg={p} badgeLabel="Trending" />)}
      </div>
    </div>
  </section>
);

const FestivalSection = ({ festival, packages }) => {
  if (!festival?.enabled || !packages?.length) return null;
  return (
    <section className="py-16 bg-secondary/5" data-testid="festival-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-600 border border-red-200 px-3 py-1 text-xs font-bold uppercase tracking-wider">
              ⏳ {festival.badge}
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-3">{festival.title}</h2>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-5 overflow-x-auto no-scrollbar pb-4 -mx-4 px-4">
          {packages.map((p) => (
            <div key={p.slug} className="w-72 shrink-0">
              <PackageCard pkg={p} badgeLabel="Festival" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CategorySection = ({ category, packages }) => {
  if (!packages?.length) return null;
  return (
    <section className="py-16" data-testid={`category-section-${category.slug}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Discover</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-1">{category.name}</h2>
          </div>
          <Link to={`/category/${category.slug}`} className="text-sm font-medium hover:text-primary transition inline-flex items-center gap-1" data-testid={`view-all-${category.slug}`}>
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.slice(0, 4).map((p) => <PackageCard key={p.slug} pkg={p} />)}
        </div>
      </div>
    </section>
  );
};


const FinalCTA = () => (
  <section className="py-24 relative overflow-hidden" data-testid="final-cta">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="rounded-[2rem] bg-card border border-border p-8 sm:p-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-primary">Ready to celebrate?</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-2 mb-4">Let's plan something unforgettable.</h2>
          <p className="text-muted-foreground mb-8 max-w-md">Tell us your event, your budget, and your dream — we'll handle the rest.</p>
          <div className="flex flex-wrap gap-3">
            <a href={waLink("Hi, please help me plan an event")} target="_blank" rel="noreferrer">
              <Button className="rounded-full bg-primary hover:bg-primary/90 gap-2 px-6 py-5" data-testid="cta-whatsapp-btn"><MessageCircle className="w-4 h-4" /> WhatsApp Now</Button>
            </a>
            <Link to="/category/couple-s-special">
              <Button variant="outline" className="rounded-full border-2 px-6 py-5 gap-2" data-testid="cta-explore-btn"><Heart className="w-4 h-4" /> Explore Romantic</Button>
            </Link>
          </div>
        </div>
        <div className="relative aspect-square rounded-3xl overflow-hidden hidden lg:block">
          <img src="https://images.unsplash.com/photo-1758705477576-8783430d55b6?auto=format&fit=crop&q=60&w=800" alt="Romantic Candle Light Dinner Setup" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [pkgsByCat, setPkgsByCat] = useState({});
  const [budgetPkgs, setBudgetPkgs] = useState([]);
  const [hotDeals, setHotDeals] = useState([]);
  const [festival, setFestival] = useState(null);
  const [festivalPkgs, setFestivalPkgs] = useState([]);

  useEffect(() => {
    document.title = "YoursDreamDecoration — Where every dream becomes a celebration";
    (async () => {
      const [cats, budget, hot, fest] = await Promise.all([
        api.get("/categories").then((r) => r.data),
        api.get("/packages?max_price=3500&limit=10").then((r) => r.data),
        api.get("/packages?tag=hot&limit=8").then((r) => r.data),
        api.get("/festival").then((r) => r.data),
      ]);
      setCategories(cats);
      setBudgetPkgs(budget);
      setHotDeals(hot);
      setFestival(fest);
      const map = {};
      await Promise.all(cats.map(async (c) => {
        const pkgs = await api.get(`/packages?category=${c.slug}&limit=4`).then((r) => r.data);
        map[c.slug] = pkgs;
      }));
      setPkgsByCat(map);
      if (fest?.enabled && fest.package_slugs?.length) {
        const fp = await Promise.all(fest.package_slugs.map((s) => api.get(`/packages/${s}`).then((r) => r.data).catch(() => null)));
        setFestivalPkgs(fp.filter(Boolean));
      }
    })();
  }, []);

  return (
    <div data-testid="homepage">
      <Hero />
      <QuickLinks />
      <FestivalSection festival={festival} packages={festivalPkgs} />
      <Marquee packages={budgetPkgs} />
      <HotDeals packages={hotDeals.length ? hotDeals : budgetPkgs} />
      {categories.map((c) => <CategorySection key={c.slug} category={c} packages={pkgsByCat[c.slug] || []} />)}
      <HowItWorks />
      <Testimonials />
      <FinalCTA />
    </div>
  );
}
